import { useMutation } from "@tanstack/react-query";
import { Check, Loader2, TicketPercent } from "lucide-react";
import { Trans } from "react-i18next";
import { featureFlags } from "@common/feature-flags";
import {
  getCouponDiscount,
  useMyCoupons,
  type MyCoupon,
} from "@frontend-common/hooks/use-my-coupons";
import { HorizontalButtonScroll } from "@/components/horizontal-button-scroll";
import { useMuiModal } from "@/components/modal";
import { applyCouponCode } from "@/features/coupons/apply-coupon";
import { useToken } from "@/hooks/use-token";
import { ErrorModal } from "@/lib/api";
import { cn, formatCurrency } from "@/lib/utils";

/** The logged-in user's coupons that apply to this machine right now, shown
 * on the payment screen as chips (same look as bb-mobile's payment screen)
 * that redeem the coupon on tap. Renders
 * nothing while the `kiosk.myCoupons` flag is off, nobody is logged in, or
 * there is nothing to offer, so the payment screen looks unchanged then. */
export function MyCouponsList({
  machineId,
  dryerBundleId,
  currency,
  activeCode,
  onApplied,
}: {
  machineId: number;
  dryerBundleId?: number;
  currency: string;
  /** Code of the coupon already redeemed for this payment, if any. */
  activeCode: string | null;
  /** Called after a tapped coupon was redeemed; the list stays busy until the
   * returned promise settles, e.g. while the payment is re-created with it. */
  onApplied: () => Promise<unknown>;
}) {
  const token = useToken();
  const enabled = featureFlags.kiosk.myCoupons && !!token;
  const coupons = useMyCoupons({ machineId }, { enabled });
  const apply = useApplyCoupon({ machineId, dryerBundleId, onApplied });

  if (!enabled || !coupons.data || coupons.data.length === 0) {
    return null;
  }

  return (
    <section className="mb-10" data-testid="my-coupons-list">
      <h3 className="mb-4 text-center text-lg font-semibold tracking-wide text-gray-500 uppercase">
        <Trans>Kuponjaid ehhez a géphez</Trans>
      </h3>
      {/* One scrolling row keeps the height fixed however many coupons the
          user holds; it stays centered while it fits, and gets arrow buttons
          once it doesn't, since dragging is unreliable on the kiosk screen. */}
      <HorizontalButtonScroll className="snap-x scroll-px-2">
        <ul className="flex w-max min-w-full justify-center gap-4 px-2">
          {coupons.data.map((coupon) => {
            const active = isSameCode(coupon.code, activeCode);
            return (
              <li key={coupon.id} className="shrink-0 snap-start">
                <CouponChip
                  coupon={coupon}
                  currency={currency}
                  state={
                    active
                      ? "active"
                      : apply.isPending && apply.variables?.id === coupon.id
                        ? "pending"
                        : "idle"
                  }
                  disabled={active || apply.isPending}
                  onClick={() => apply.mutate(coupon)}
                />
              </li>
            );
          })}
        </ul>
      </HorizontalButtonScroll>
    </section>
  );
}

function useApplyCoupon({
  machineId,
  dryerBundleId,
  onApplied,
}: {
  machineId: number;
  dryerBundleId?: number;
  onApplied: () => Promise<unknown>;
}) {
  const { showModal } = useMuiModal();

  return useMutation({
    mutationFn: async (coupon: MyCoupon) => {
      const response = await applyCouponCode({
        code: coupon.code,
        machineId,
        dryerBundleId,
        source: "my-coupons",
      });

      if (response.type === "error") {
        showModal(
          <ErrorModal
            errorKey={response.error}
            translatedMessage={response.translatedMessage}
          />,
        );
        return;
      }

      await onApplied();
    },
  });
}

function CouponChip({
  coupon,
  currency,
  state,
  disabled,
  onClick,
}: {
  coupon: MyCoupon;
  currency: string;
  state: "idle" | "pending" | "active";
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={state === "active"}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex items-center gap-4 rounded-2xl border-2 border-gray-200 bg-white px-6 py-4 transition-opacity",
        state === "active" && "border-bub-dark-blue bg-[#EAF2FB]",
        state === "idle" && disabled && "opacity-50",
      )}
    >
      {state === "active" ? (
        <span className="flex size-8 items-center justify-center rounded-full bg-bub-dark-blue">
          <Check className="size-5" color="white" strokeWidth={3.5} />
        </span>
      ) : state === "pending" ? (
        <Loader2 className="size-8 animate-spin text-bub-dark-blue" />
      ) : (
        <TicketPercent className="size-8 text-bub-dark-blue" />
      )}
      <span className="font-mono text-3xl leading-none font-semibold tracking-wider text-[#094A73]">
        {coupon.code}
      </span>
      <span className="text-3xl leading-none font-bold text-bub-dark-blue">
        −{formatDiscount(coupon, currency)}
      </span>
    </button>
  );
}

function isSameCode(code: string, activeCode: string | null) {
  return !!activeCode && code.toUpperCase() === activeCode.toUpperCase();
}

function formatDiscount(coupon: MyCoupon, currency: string) {
  const discount = getCouponDiscount(coupon, currency);
  return discount.kind === "fixed"
    ? formatCurrency(discount.amount, discount.currency)
    : `${discount.value}%`;
}
